/**
 * `AIGuardrailsModel.ts` — port of Unity's `AIGuardrailsModel.cs` +
 * `.Permission.cs` + `.Interaction.cs` + `.Notice.cs`.
 *
 * Upstream deleted `AIGuardrailsModel.ToolValidation.cs`: argument
 * discipline is prompt-owned ("the runtime no longer second-guesses tool
 * arguments"), and ambiguity resolution goes through multi-round
 * `present_options`. The hard runtime guardrails that remain:
 *
 *   1. Permission — `writerOnly` tools are denied for viewers, and live agent
 *      writes are denied during Workflow playback. Authored Workflow calls
 *      use the transient mutation boundary instead, and during a RUN the
 *      step-scoped tools pass both gates because the run undoes them at the
 *      step change (see `authorizeToolExecution`).
 *      A viewer-open tool with a writing half asks `authorizeProjectWrite`.
 *      The comment tools (`commenterAllowed`) pass the playback refusal and
 *      keep the role check: comments persist through a run by design.
 *   2. Interaction — `present_options` rounds (max 6 options each, deduped)
 *      + `Listen_User` push-to-talk handoff.
 *   3. Notices — connection/error notices surfaced to the conversation UI.
 *   4. Step moves (web-only, AI-1412) — the agent moves a guided run only when
 *      the learner's last turn asked for it. Its own file:
 *      `AIGuardrailsModel.StepMove.ts`, reached through `stepMoves`.
 */

import type { AIAgentTool } from "@ai/registry/AIAgentToolRegistryModel";
import { TOOL_TRIGGER } from "@ai/registry/AIAgentToolRegistryModel";
import { isStepScopedTool } from "@ai/registry/scriptCommandCatalog";
import type {
  AIConversationPresentationModel,
  AIPresentOptionsRound,
} from "@ai/interface/AIConversationPresentationModel";
import { StepMoveGuardrail, type IStepMoveGuardrail } from "./AIGuardrailsModel.StepMove";

/** Why a project write was refused — the code `denialMessage` opens with. */
export type GuardrailWriteDenialReason = "permission_denied" | "workflow_playback_read_only";

export interface AIGuardrailToolAuthorizationResult {
  readonly isAllowed: boolean;
  /** Denial string returned to the model as the tool output. */
  readonly denialMessage?: string;
  /** Set with `denialMessage`, for a caller that words its own result. */
  readonly denialReason?: GuardrailWriteDenialReason;
}

export type GuardrailNoticeSeverity = "info" | "warning" | "error";

export interface IAIGuardrailsModel {
  /** Whether the learner asked for a step move — see `AIGuardrailsModel.StepMove.ts`. */
  readonly stepMoves: IStepMoveGuardrail;
  authorizeToolExecution(
    tool: AIAgentTool | null,
    toolName: string,
    triggeredBy?: string,
  ): AIGuardrailToolAuthorizationResult;
  /**
   * The `writerOnly` gate for one EFFECT rather than a whole tool — see the
   * implementation. `toolName` names the writer tool whose effect it is.
   */
  authorizeProjectWrite(
    toolName: string,
    triggeredBy?: string,
    commenterAllowed?: boolean,
  ): AIGuardrailToolAuthorizationResult;
  tryPresentOptions(rounds: ReadonlyArray<AIPresentOptionsRound>, moreInfoRequired: boolean): string;
  wereOptionsRecentlyPresented(): boolean;
  tryTriggerListenUser(): string;
  publishGuardrailNotice(message: string, severity: GuardrailNoticeSeverity): void;
}

const MAX_OPTIONS_PER_ROUND = 6;
/** Unity's "options recently presented" suppression window. */
const OPTIONS_RECENCY_WINDOW_MS = 3000;

export class AIGuardrailsModel implements IAIGuardrailsModel {
  readonly stepMoves: IStepMoveGuardrail;
  private readonly m_isWriter: () => boolean;
  private readonly m_canComment: () => boolean;
  private readonly m_isWorkflowPlaybackActive: () => boolean;
  private readonly m_isRunUndoingLiveChanges: () => boolean;
  private readonly m_presentation: AIConversationPresentationModel;
  private readonly m_requestListenUser: () => boolean;
  private m_lastOptionsPresentedAt = 0;

  /**
   * `isRunUndoingLiveChanges` is true while a guided run captures Spark's own
   * changes to undo them at the step change (`WorkflowScriptRunner.
   * isCapturingLiveChanges`). `logStepMoveCheck` receives one line per
   * step-move check, for the debug Log tab. `canComment` is the Comment
   * tool's role rule (Owner, Editor or Commenter); it defaults to `isWriter`.
   */
  constructor(
    isWriter: () => boolean,
    presentation: AIConversationPresentationModel,
    requestListenUser: () => boolean,
    isWorkflowPlaybackActive: () => boolean,
    isRunUndoingLiveChanges: () => boolean = () => false,
    logStepMoveCheck: ((summary: string) => void) | null = null,
    canComment: () => boolean = isWriter,
  ) {
    this.m_isWriter = isWriter;
    this.m_canComment = canComment;
    this.m_isWorkflowPlaybackActive = isWorkflowPlaybackActive;
    this.m_isRunUndoingLiveChanges = isRunUndoingLiveChanges;
    this.m_presentation = presentation;
    this.m_requestListenUser = requestListenUser;
    this.stepMoves = new StepMoveGuardrail(logStepMoveCheck);
  }

  /**
   * `AIGuardrailsModel.Permission` — the `writerOnly` role gate.
   *
   * ⚠ **Workflow playback is exempt, and that is the whole point of the
   * `triggeredBy` argument.** This gate asks "may the AGENT change the project
   * on this user's behalf?", and a workflow script step is not the agent
   * deciding anything: it is content an AUTHOR wrote into `workflow.yaml`,
   * replayed verbatim. Only a writer can save that file, so the authorization
   * question was already answered — by the author, at authoring time. Held to
   * this gate instead, every scene command in every guided lesson
   * (`hide_parts`, `isolate`, `highlight_part`, `Toggle_Model_Animation`, …)
   * was refused for exactly the audience guided training exists for, and
   * refused SILENTLY: `WorkflowScriptRunner` logs a warning and keeps playing,
   * so the learner saw a lesson narrate steps that never happened.
   *
   * The bypass remains BLANKET rather than scoped to the view-only command
   * vocabulary (`K_SCRIPT_DOCUMENTED_TOOL_IDS`) — user direction, 2026-08-31,
   * "commands in workflow should be able to execute regardless of user role".
   * Persistence is a separate concern: `WorkflowScriptRunner` opens a
   * ProjectModel mutation scope that applies supported commands locally while
   * suppressing database writes, project broadcasts, and thumbnails. It also
   * refuses structural commands that have no transient overlay.
   * A live agent call gets no such exemption while the scope is active: even
   * an editor's writer tool is refused so it cannot persist state outside the
   * script capture ledger during playback.
   *
   * **Except the step-scoped tools during a RUN** (AI-1424, user direction
   * 2026-09-29): hide/unhide, isolate and highlight, and clearing them, which
   * learners must be able to ask Spark for — and `Toggle_Model_Animation`, for
   * "replay the animation" (AI-1459, 2026-10-05). They pass BOTH gates — this one and
   * the viewer gate below — because while a run plays `WorkflowScriptRunner`
   * captures each such call and puts it back at the step change, and the scope
   * keeps it out of the database; nothing a viewer asks for outlives the step.
   * Keyed on the RUNNER's "am I capturing" rather than on the scope, so the
   * editor's single-script preview, which opens the scope but is not a run and
   * has no step change to undo at, still refuses them. Before this, the
   * Guide's `isolate`/`highlight_part`/`clear_*` were refused on every run.
   *
   * ⚠ The exemption is the TOOL's, decided here, and deliberately NOT part of
   * {@link authorizeProjectWrite}. `focus_on_part` asks that method for its
   * fade by naming `isolate`, and its fade must stay refused during a run and
   * for a viewer (user direction 2026-09-29: `focus_on_part` does not fade the
   * scene for a learner or in preview — a learner who wants the fade asks for
   * `isolate`). So during a run the `isolate` TOOL is allowed while the
   * `isolate` EFFECT of another tool is not: the one place that method's
   * "refused exactly when that tool is" does not hold, on purpose.
   *
   * An OMITTED `triggeredBy` is never a bypass: the default is the agent's, so
   * a future caller that forgets to say who it is fails closed.
   */
  authorizeToolExecution(
    tool: AIAgentTool | null,
    toolName: string,
    triggeredBy: string = TOOL_TRIGGER.AI,
  ): AIGuardrailToolAuthorizationResult {
    if (!tool?.writerOnly) return { isAllowed: true };
    if (isStepScopedTool(toolName) && this.m_isRunUndoingLiveChanges()) {
      return { isAllowed: true };
    }
    return this.authorizeProjectWrite(toolName, triggeredBy, tool.commenterAllowed === true);
  }

  /**
   * The rules `writerOnly` stands for, asked of one EFFECT instead of a whole
   * tool: workflow playback is exempt, a live agent may not write while a run
   * owns the scene, and a viewer may not write at all.
   *
   * ⚠ **A tool that is open to viewers but has a writing HALF must ask this
   * for that half.** `writerOnly` is per tool, so the gate above never sees
   * it. `focus_on_part` is the case that shipped (2026-09-29): framing the
   * camera is a viewer's to ask for, but its `useIsolate` fade is the
   * `isolate` tool's effect — persisted to the scene row and broadcast to
   * every peer — and it reached both for a viewer, and outside the script's
   * revert ledger during a run. Flipping the whole tool to `writerOnly` would
   * have taken "show me X" away from the people guided training is for, so
   * the executor drops the half and says so instead. Name the writer tool
   * whose effect it is, so the half is refused exactly when that tool is —
   * except the step-scoped exemption during a run, which belongs to the tool
   * and not to this method (see {@link authorizeToolExecution}).
   *
   * `commenterAllowed` widens the role check to a Commenter, for a tool that
   * does only what the Comment tool lets a Commenter do (web-only; Unity's
   * `create_annotation` is writer-only).
   *
   * ⚠ It also lifts the playback refusal (user direction, 2026-11-02: a
   * learner who can comment may ask Spark to leave a comment during a run, and
   * an author may during preview). That refusal keeps a live write from
   * outliving the run's capture ledger, and a comment is meant to outlive it:
   * the annotations model persists and broadcasts it outside the playback
   * mutation scope, the run neither captures nor reverts it, and it stays until
   * someone resolves it. The role check below still applies, so a Viewer is
   * refused during a run as everywhere else.
   */
  authorizeProjectWrite(
    toolName: string,
    triggeredBy: string = TOOL_TRIGGER.AI,
    commenterAllowed = false,
  ): AIGuardrailToolAuthorizationResult {
    if (triggeredBy === TOOL_TRIGGER.WORKFLOW) return { isAllowed: true };
    if (this.m_isWorkflowPlaybackActive() && !commenterAllowed) {
      return {
        isAllowed: false,
        denialReason: "workflow_playback_read_only",
        denialMessage:
          `error: workflow_playback_read_only. '${toolName}' modifies shared project data and ` +
          "cannot run while Workflow playback is active.",
      };
    }
    if (this.m_isWriter()) return { isAllowed: true };
    const isCommenter = this.m_canComment();
    if (commenterAllowed && isCommenter) return { isAllowed: true };
    // Name the role the user actually has: a Commenter told they are a viewer
    // is told something false about their own account.
    const role = isCommenter ? "a commenter" : "a viewer";
    const needed = commenterAllowed ? "commenter or editor access" : "editor access";
    return {
      isAllowed: false,
      denialReason: "permission_denied",
      denialMessage:
        `error: permission_denied. The current user is ${role} and '${toolName}' modifies the project. ` +
        `Tell the user they need ${needed} for this action.`,
    };
  }

  tryPresentOptions(
    rounds: ReadonlyArray<AIPresentOptionsRound>,
    moreInfoRequired: boolean,
  ): string {
    const sanitized: AIPresentOptionsRound[] = [];
    for (const round of rounds) {
      const question = round.question?.trim();
      const seen = new Set<string>();
      const options: string[] = [];
      for (const raw of round.options ?? []) {
        const option = raw?.trim();
        if (!option) continue;
        const key = option.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        options.push(option);
        if (options.length >= MAX_OPTIONS_PER_ROUND) break;
      }
      if (!question || options.length < 2) continue;
      sanitized.push({ question, options });
    }

    if (sanitized.length === 0) {
      return "error: present_options_requires_at_least_one_round_with_a_question_and_2_options";
    }

    this.m_presentation.publishPresentOptions(sanitized, moreInfoRequired);
    this.m_lastOptionsPresentedAt = Date.now();
    return "ok: options presented to the user. End your response now; the user's picks arrive as their next message.";
  }

  wereOptionsRecentlyPresented(): boolean {
    return Date.now() - this.m_lastOptionsPresentedAt <= OPTIONS_RECENCY_WINDOW_MS;
  }

  tryTriggerListenUser(): string {
    const triggered = this.m_requestListenUser();
    return triggered
      ? "ok: microphone opened for the user's voice reply"
      : "ok: microphone unchanged (always-on listening is active or this surface keeps one-shot listening under user control)";
  }

  publishGuardrailNotice(message: string, severity: GuardrailNoticeSeverity): void {
    this.m_presentation.publishGuardrailNotice(message, severity);
  }
}

export const AI_GUARDRAILS_MODEL = Symbol.for("Campfire.AIGuardrailsModel");
